import type { ReactNode } from 'react'
import { useContext } from 'react'

import { nanoid } from 'nanoid'

import { RadioCardStyled } from './styles/RadioCardStyled'
import { RadioGroupContext } from '../../RadioGroupContext'
import {
  InfoBadge,
  InfoBadgeTypes,
  type InfoBadgeType,
  StatusBadge,
  type StatusBadgeStatus,
} from '@ui/elements/Badge'
import { Space } from '@ui/elements/Space'
import { Text } from '@ui/elements/Text'

export interface IRadioCardProps {
  value: string
  label: string
  isDisabled?: boolean
  description: string | ReactNode
  infoBadgeText?: string
  infoBadgeType?: InfoBadgeType
  statusBadgeText?: string
  statusBadgeType?: StatusBadgeStatus
  /**
   * @temporary - Added for one-off use case to display an additional InfoBadge
   * alongside the primary infoBadgeText
   */
  additionalInfoBadgeText?: string
}

export const RadioCard = ({
  label,
  value: radioValue,
  isDisabled = false,
  description = null,
  infoBadgeText,
  infoBadgeType = InfoBadgeTypes.LOW_PRIORITY,
  statusBadgeText,
  statusBadgeType,
  additionalInfoBadgeText,
}: IRadioCardProps) => {
  const labelId = `id-${nanoid()}-label`

  const { name, value, onChange } = useContext(RadioGroupContext)

  const descriptionContent =
    typeof description === 'string' ? (
      <Text>{description}</Text>
    ) : (
      <>{description}</>
    )

  return (
    <RadioCardStyled isDisabled={isDisabled}>
      <Space direction='horizontal' size='small'>
        <input
          type='radio'
          name={name}
          aria-labelledby={labelId}
          checked={value === radioValue}
          value={radioValue}
          onChange={onChange}
          disabled={isDisabled}
        />

        <Space size='small'>
          <Space direction='horizontal' size='small' align='center'>
            <div id={labelId}>
              <strong>{label}</strong>
            </div>

            {infoBadgeText ? (
              <InfoBadge
                type={infoBadgeType}
                text={infoBadgeText}
                size='small'
              />
            ) : null}

            {additionalInfoBadgeText ? (
              <InfoBadge
                type={InfoBadgeTypes.LOW_PRIORITY}
                text={additionalInfoBadgeText}
                size='small'
              />
            ) : null}

            {statusBadgeText && statusBadgeType ? (
              <StatusBadge
                type={statusBadgeType}
                text={statusBadgeText}
                size='small'
              />
            ) : null}
          </Space>

          {descriptionContent}
        </Space>
      </Space>
    </RadioCardStyled>
  )
}
